import { ICleanupMark, IDownstreamTargetStatus, IHeartbeatModel, IStatusTableModel, ITableAudit } from './sync-monitoring.models';
import { isTargetSynchronized } from './sync-monitoring.utils';

/** Core-one's audit statuses, worst first (TableAuditOverallStatus). */
export const AUDIT_STATUS_ORDER = ['differing', 'could_not_verify', 'check_failed', 'too_large_to_audit', 'generation_changed', 'target_rebuild_in_progress', 'engine_bootstrap_failed', 'not_yet_checkable', 'too_volatile', 'in_progress', 'never_audited', 'data_one_only', 'aligned'];

// Statuses where the audit could not confirm that the table matches core-engine.
const AUDIT_ISSUE_STATUSES = new Set(['differing', 'could_not_verify', 'check_failed', 'too_large_to_audit', 'generation_changed', 'engine_bootstrap_failed']);

/** The audit kinds of findings, in the order the Audit tab shows them, with the run count that says whether a kind has any. */
export const AUDIT_FINDING_KINDS: { kind: string; count: (audit: ITableAudit) => number | null | undefined }[] = [
	{ kind: 'auditRepairExhausted', count: (audit) => audit.exhaustedCount },
	{ kind: 'auditDriftMissingInEngine', count: (audit) => audit.missingCount },
	{ kind: 'auditDriftRowVersionMismatch', count: (audit) => audit.mismatchedCount },
	{ kind: 'auditDriftExtraInEngine', count: (audit) => audit.extraCount },
	{ kind: 'auditDriftOffVersion', count: (audit) => audit.offVersionCount },
];

/** The table's audit status; a table the API sends no audit for has never been audited. */
export function auditStatusOf(audit: ITableAudit | null | undefined): string {
	return audit?.status ?? 'never_audited';
}

/** Sort rank of an audit status, worst first; an unknown status sorts last. */
export function auditStatusRank(status: string): number {
	const index = AUDIT_STATUS_ORDER.indexOf(status);
	return index < 0 ? AUDIT_STATUS_ORDER.length : index;
}

/** Whether the table has an engine table and so can be audited. The status lists a core-engine target for every table of the version's engine list, whatever the table's last audit says: a table not stamped yet has been audited as `data_one_only`, and going by that would never let it be audited again. */
export function hasEngineTable(table: IStatusTableModel): boolean {
	return findEngineTarget(table.targets) !== null;
}

/** Whether the audit status does not confirm that the table matches core-engine. */
export function isAuditIssueStatus(status: string): boolean {
	return AUDIT_ISSUE_STATUSES.has(status);
}

/** Whether the table has an audit issue: a status that does not confirm the table, or items repair gave up on. */
export function isAuditIssue(audit: ITableAudit | null | undefined): boolean {
	return isAuditIssueStatus(auditStatusOf(audit)) || (audit?.exhaustedCount ?? 0) > 0;
}

/** The table's core-engine target, or null when the table has none (One only). */
export function findEngineTarget(targets: IDownstreamTargetStatus[] | null | undefined): IDownstreamTargetStatus | null {
	return targets?.find((target) => target.name?.toLowerCase() === 'core-engine') ?? null;
}

/** Whether the table is not (yet) in core-engine: no engine target, or one that is not synchronized. */
export function isNotInEngine(target: IDownstreamTargetStatus | null): boolean {
	return !target || !isTargetSynchronized(target);
}

/** Items the last audit found but did not replay, which "Replay skipped items" can send: the items over the replay cap, or every missing and mismatched item when replay is off. Only a differing table that has not been replayed since has any. The dry run gives the exact figure. */
export function skippedCount(audit: ITableAudit | null | undefined, replayEnabled: boolean | null | undefined): number {
	if (auditStatusOf(audit) !== 'differing' || audit?.replayedSkipped) {
		return 0;
	}
	if (replayEnabled === false) {
		return (audit?.missingCount ?? 0) + (audit?.mismatchedCount ?? 0);
	}
	return audit?.deferredCount ?? 0;
}

export interface IAuditTotals {
	missing: number;
	mismatch: number;
	extra: number;
	offVersion: number;
	replayed: number;
	failed: number;
	deferred: number;
	exhausted: number;
}

/** Sums the findings and repair counts of every table's last completed run. */
export function sumAuditTotals(audits: (ITableAudit | null | undefined)[]): IAuditTotals {
	const totals: IAuditTotals = { missing: 0, mismatch: 0, extra: 0, offVersion: 0, replayed: 0, failed: 0, deferred: 0, exhausted: 0 };
	for (const audit of audits) {
		totals.missing += audit?.missingCount ?? 0;
		totals.mismatch += audit?.mismatchedCount ?? 0;
		totals.extra += audit?.extraCount ?? 0;
		totals.offVersion += audit?.offVersionCount ?? 0;
		totals.replayed += audit?.replayedCount ?? 0;
		totals.failed += audit?.failedCount ?? 0;
		totals.deferred += audit?.deferredCount ?? 0;
		totals.exhausted += audit?.exhaustedCount ?? 0;
	}
	return totals;
}

export interface IMarkGroup {
	version: string;
	runId: string;
	expiresAt: number | null;
	marks: ICleanupMark[];
}

/** Groups expiry marks by the version and run that marked them: a request to clear marks names exactly one of each. */
export function groupMarks(marks: ICleanupMark[] | null | undefined): IMarkGroup[] {
	const groups = new Map<string, IMarkGroup>();
	for (const mark of marks ?? []) {
		const version = mark.observedVersion ?? '';
		const runId = mark.auditRunId ?? '';
		const id = `${version}|${runId}`;
		const group = groups.get(id) ?? { version, runId, expiresAt: null, marks: [] };
		group.marks.push(mark);
		if (mark.markExpiresAt != null && (group.expiresAt == null || mark.markExpiresAt < group.expiresAt)) {
			group.expiresAt = mark.markExpiresAt;
		}
		groups.set(id, group);
	}
	return [...groups.values()];
}

/** Formats a process uptime in seconds compactly. Examples: 42 -> "42s", 3700 -> "1h 1m", 90061 -> "1d 1h 1m" */
export function formatUptime(seconds: number | null | undefined): string {
	if (seconds == null || !Number.isFinite(seconds) || seconds < 0) {
		return '—';
	}
	const whole = Math.floor(seconds);
	const days = Math.floor(whole / 86400);
	const hours = Math.floor((whole % 86400) / 3600);
	const minutes = Math.floor((whole % 3600) / 60);
	const secs = whole % 60;
	if (days > 0) {
		return `${days}d ${hours}h ${minutes}m`;
	}
	if (hours > 0) {
		return `${hours}h ${minutes}m`;
	}
	return minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`;
}

/** Whether the heartbeat at `index` (newest first) comes from a new agent process: its PID differs from the heartbeat before it, or its uptime is lower. */
export function isHeartbeatRestart(heartbeats: IHeartbeatModel[], index: number): boolean {
	const current = heartbeats[index];
	const previous = heartbeats[index + 1];
	if (!current || !previous) {
		return false;
	}
	if (current.pid != null && previous.pid != null && current.pid !== previous.pid) {
		return true;
	}
	return current.timeSpentAlive != null && previous.timeSpentAlive != null && current.timeSpentAlive < previous.timeSpentAlive;
}
